import type { ReactElement } from "react";

const LINE_COLOR = "#787878";
const NODE_SPACING = 36;
const REORDER_OFFSET = 32;
const SELECTION_CELL_OFFSET = 48;
const CORNER_RADIUS = 4;

export const CONNECTOR_NODE_SPACING = NODE_SPACING;

// x coordinate (relative to the row container) of the vertical column line at
// the given indentation level. single source of truth: every connector
// segment (verticals, l-corners, downward extension) anchors here.
function columnX(
  level: number,
  canReorder: boolean,
  hasSelectionCell: boolean,
  customVerticalOffset: number = 29,
): number {
  return (
    (hasSelectionCell ? SELECTION_CELL_OFFSET : 0) +
    level * NODE_SPACING +
    customVerticalOffset +
    (canReorder ? REORDER_OFFSET : 0)
  );
}

interface RowConnectorsProps {
  readonly level: number;
  readonly parentConnectors: boolean[];
  readonly canReorder: boolean;
  readonly hasExpandedChildren: boolean;
  readonly hasSelectionCell: boolean;
  readonly customVerticalOffset?: number;
}

// paints all connector segments for one row in a single coordinate system.
// each ancestor whose `parentConnectors[i]` is true gets a full-height
// vertical; the immediate-parent column gets either a full-height vertical
// (if this row is not the last child) or a rounded l-corner (if it is). when
// this row is itself an expanded parent, a downward stub bridges its mid-row
// to the children below.
export function RowConnectors({
  level,
  parentConnectors,
  canReorder,
  hasExpandedChildren,
  hasSelectionCell,
  customVerticalOffset,
}: RowConnectorsProps) {
  const segments: ReactElement[] = [];

  for (let i = 0; i < level; i++) {
    if (!parentConnectors[i]) continue;
    segments.push(
      <div
        key={`v-${i}`}
        className="pointer-events-none absolute"
        style={{
          left: columnX(i, canReorder, hasSelectionCell, customVerticalOffset),
          top: 0,
          bottom: 0,
          width: 1,
          background: LINE_COLOR,
        }}
      />,
    );
  }

  if (level > 0) {
    const parentCol = columnX(
      level - 1,
      canReorder,
      hasSelectionCell,
      customVerticalOffset,
    );
    const myCol = columnX(
      level,
      canReorder,
      hasSelectionCell,
      customVerticalOffset,
    );
    const isLast = !parentConnectors[level - 1];

    if (isLast) {
      segments.push(
        <div
          key="l-corner"
          className="pointer-events-none absolute"
          style={{
            left: parentCol,
            top: 0,
            height: "50%",
            width: myCol - parentCol,
            borderLeft: `1px solid ${LINE_COLOR}`,
            borderBottom: `1px solid ${LINE_COLOR}`,
            borderBottomLeftRadius: CORNER_RADIUS,
          }}
        />,
      );
    } else {
      segments.push(
        <div
          key="branch-h"
          className="pointer-events-none absolute"
          style={{
            left: parentCol,
            top: "50%",
            width: myCol - parentCol,
            height: 1,
            background: LINE_COLOR,
          }}
        />,
      );
    }
  }

  if (hasExpandedChildren) {
    segments.push(
      <div
        key="downward"
        className="pointer-events-none absolute"
        style={{
          left: columnX(
            level,
            canReorder,
            hasSelectionCell,
            customVerticalOffset,
          ),
          top: "50%",
          bottom: 0,
          width: 1,
          background: LINE_COLOR,
        }}
      />,
    );
  }

  return <>{segments}</>;
}
